import {
  ArrowTopRightOnSquareIcon,
  Cog6ToothIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  WorkflowTooltip,
  WorkflowTooltipWrap,
} from "@via/components/tooltip/workflow-tooltip.tsx";
import {
  HandleTypes,
  WorkflowEditTypes,
} from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import {
  divideSemiCircle,
  getHandleClass,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { NODE_TYPES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers";
import { TooltipInput } from "@via/utils/interfaces.tsx";
import { Handle, NodeToolbar, Position, useReactFlow } from "@xyflow/react";
import { type JSX, memo, useMemo } from "react";

const ModuleNode = ({
  data,
  id,
  onDelete,
  onEditModule,
  onEditDisplayName,
  onModuleOpen,
  onHandleHover,
  onHandleLeave,
  isEditingAllowed,
}: any): JSX.Element => {
  const { getNode, getEdges } = useReactFlow();
  const edges = getEdges();
  const radius = 54;
  const sourcePoints = useMemo(
    () => divideSemiCircle("source", radius, data?.outputHandles.length),
    [data?.outputHandles]
  );
  const targetPoints = useMemo(
    () => divideSemiCircle("target", radius, data?.inputHandles.length),
    [data?.inputHandles]
  );
  // Check if a specific handle is connected
  const isHandleConnected = useMemo(
    () => (handle: string, type: "source" | "target") => {
      if (type === "source") {
        return edges.some(
          edge => edge.source === id && edge.sourceHandle === handle
        );
      } else {
        return edges.some(
          edge => edge.target === id && edge.targetHandle === handle
        );
      }
    },
    [edges, id]
  );
  const isValidSourceConnection = (connection, file): boolean => {
    if (!connection) return false;

    const { source, sourceHandle } = connection;
    const node: any = getNode(source);

    if (!node) return false;
    if (node?.data?.outputHandles[0].includes("inPara")) {
      return true;
    }
    const outputHandles = node.data.outputHandles as unknown as string[];
    const tooltips = node.data.tooltips as unknown as {
      outputs: TooltipInput[];
    };

    const match = outputHandles.indexOf(sourceHandle as string);
    if (match === -1 || match === undefined) return false;

    return tooltips.outputs[match]?.fileType === file;
  };
  const isValidTargetConnection = (connection, file): boolean => {
    if (!connection) return false;

    const { target, targetHandle } = connection;
    const node: any = getNode(target);

    if (!node) return false;
    if (node?.data?.inputHandles[0].includes("outPara")) {
      return true;
    }
    const inputHandles = node.data.inputHandles as unknown as string[];
    const tooltips = node.data.tooltips as unknown as {
      inputs: TooltipInput[];
    };

    const match = inputHandles.indexOf(targetHandle as string);
    if (match === -1 || match === undefined) return false;

    return tooltips.inputs[match]?.fileType === file;
  };
  return (
    <div className={`outer-circle-module ${data?.className}`}>
      <NodeToolbar
        offset={16}
        className={classNames(
          "flex flex-row rounded-md border-[1px] border-gray-300 bg-white p-2",
          !isEditingAllowed ? "pr-0" : ""
        )}>
        <ArrowTopRightOnSquareIcon
          className="w-[26px] cursor-pointer pr-2 hover:text-gray-600"
          onClick={() => onModuleOpen(data?.pipelineId)}
        />
        <Cog6ToothIcon
          className="w-[26px] cursor-pointer pr-2 hover:text-gray-600"
          onClick={() => onEditModule(WorkflowEditTypes.EditModule, data)}
        />
        {isEditingAllowed && (
          <>
            <PencilIcon
              className="w-[20px] cursor-pointer pr-1 hover:text-gray-600"
              onClick={() => onEditDisplayName(data)}
            />
            <TrashIcon
              className="w-[20px] cursor-pointer pl-1 hover:text-gray-600"
              onClick={() => onDelete(id)}
            />
          </>
        )}
      </NodeToolbar>
      <div title={data?.label} className="px-2 text-sm text-center">
        {data.label?.replace(/_/g, " ")}
      </div>
      {data?.outputHandles
        .slice()
        .reverse()
        .map((handle, i) => {
          // because we reversed the outputHandles, now this is the correct index for the tooltips
          const originalIndex = data.outputHandles.length - 1 - i;
          const handleConnected = isHandleConnected(handle, "source");
          return (
            <WorkflowTooltipWrap key={`${i}-${handle}`}>
              <Handle
                id={handle}
                type="source"
                className={getHandleClass(
                  data,
                  data?.tooltips?.outputs[originalIndex]?.fileType,
                  handle,
                  HandleTypes.Source,
                  id,
                  handleConnected
                )}
                isValidConnection={connection =>
                  isValidTargetConnection(
                    connection,
                    data?.tooltips?.outputs[originalIndex]?.fileType
                  )
                }
                position={Position.Right}
                onMouseEnter={() =>
                  onHandleHover(
                    data?.tooltips?.outputs[originalIndex]?.fileType,
                    HandleTypes.Source,
                    handle,
                    id,
                    NODE_TYPES.ModuleNode,
                    handleConnected
                  )
                }
                onMouseLeave={onHandleLeave}
                style={{
                  top: `${sourcePoints[i].y + 50}%`,
                  left: `${sourcePoints[i].x + 40}%`,
                  background: "steelblue",
                }}>
                <WorkflowTooltip
                  data={data?.tooltips?.outputs[originalIndex]}
                />
              </Handle>
            </WorkflowTooltipWrap>
          );
        })}
      {data?.inputHandles
        .slice()
        .reverse()
        .map((handle, i) => {
          // because we reversed the inputHandles, now this is the correct index for the tooltips
          const originalIndex = data.inputHandles.length - 1 - i;
          const handleConnected = isHandleConnected(handle, "target");
          return (
            <WorkflowTooltipWrap key={`${i}-${handle}`}>
              <Handle
                id={handle}
                type="target"
                className={getHandleClass(
                  data,
                  data?.tooltips?.inputs[originalIndex]?.fileType,
                  handle,
                  HandleTypes.Target,
                  id,
                  handleConnected
                )}
                isValidConnection={connection =>
                  isValidSourceConnection(
                    connection,
                    data?.tooltips?.inputs[originalIndex]?.fileType
                  )
                }
                position={Position.Left}
                onMouseEnter={() =>
                  onHandleHover(
                    data?.tooltips?.inputs[originalIndex]?.fileType,
                    HandleTypes.Target,
                    handle,
                    id,
                    NODE_TYPES.ModuleNode,
                    handleConnected
                  )
                }
                onMouseLeave={onHandleLeave}
                style={{
                  top: `${targetPoints[i].y + 50}%`,
                  left: `${targetPoints[i].x + 46}%`,
                  background: "tomato",
                }}>
                <WorkflowTooltip data={data?.tooltips?.inputs[originalIndex]} />
              </Handle>
            </WorkflowTooltipWrap>
          );
        })}
    </div>
  );
};
export default memo(ModuleNode);
